import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, FileDown, GraduationCap, Search, Wrench } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs, { breadcrumbSchema } from "@/components/Breadcrumbs";
import { KineticLines, Reveal, SectionHead } from "@/components/Motion";
import CTASection from "@/components/CTASection";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const TOPICS = ["Cleanroom Fundamentals", "Equipment Knowledge"];

export default function KnowledgeCenter() {
  const [articles, setArticles] = useState([]);
  const [docs, setDocs] = useState([]);
  const [glossary, setGlossary] = useState([]);
  const [gQuery, setGQuery] = useState("");

  useEffect(() => {
    api.get("/knowledge").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/glossary").then((r) => setGlossary(r.data)).catch(() => {});
  }, []);

  const filteredGlossary = useMemo(() => {
    const q = gQuery.trim().toLowerCase();
    if (!q) return glossary;
    return glossary.filter((g) => g.term.toLowerCase().includes(q) || g.definition.toLowerCase().includes(q));
  }, [glossary, gQuery]);

  return (
    <div data-testid="knowledge-center-page">
      <SEO title="Knowledge Center — Cleanroom Technology Resources" path="/knowledge-center"
        description="Technical resources from STJ Innovations: cleanroom fundamentals, equipment knowledge, technical guides and a searchable cleanroom engineering glossary."
        jsonLd={breadcrumbSchema([{ label: "Knowledge Center" }])} />

      <section className="blueprint-dark bg-ink">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Breadcrumbs dark items={[{ label: "Knowledge Center" }]} />
          <p className="eyebrow eyebrow-light">Knowledge Center</p>
          <KineticLines lines={["Cleanroom Technology,", "Explained Properly"]}
            className="mt-5 font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl" />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
            A structured technical library for engineers, consultants, students, researchers and pharmaceutical professionals — cleanroom fundamentals, equipment knowledge, technical guides and a searchable glossary.
          </p>
        </div>
      </section>

      {/* Topic sections */}
      {TOPICS.map((topic) => {
        const items = articles.filter((a) => a.topic === topic);
        if (!items.length) return null;
        return (
          <section key={topic} className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid={`kc-topic-${topic.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-3">
              {topic === "Cleanroom Fundamentals" ? <BookOpen size={20} className="text-accent" /> : <Wrench size={20} className="text-accent" />}
              <h2 className="font-display text-2xl font-semibold tracking-tight text-ink">{topic}</h2>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((a) => (
                <Link key={a.slug} to={`/knowledge-center/${a.slug}`} data-testid={`kc-article-${a.slug}`}
                  className="group flex h-full flex-col border border-line bg-white p-6 transition-colors hover:border-accent/40">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent">{a.topic}</p>
                  <h3 className="mt-2.5 font-display text-lg font-semibold leading-snug tracking-tight text-ink transition-colors group-hover:text-accent">
                    {a.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-steel line-clamp-3">{a.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-accent">
                    Read <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        );
      })}

      {/* Technical guides & downloads */}
      <section className="border-y border-line bg-mist" data-testid="kc-guides">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Technical Guides" title="Documents & Downloads"
            copy="Technical notes, application guides and product literature published by STJ for public use. New documents are added through our admin library." />
          <div className="mt-10 grid gap-4 sm:grid-cols-2" data-testid="kc-documents-grid">
            <a href="/STJ-Innovations-Pharmaceutical-Cleanroom-Equipment-Catalogue.pdf" target="_blank" rel="noopener noreferrer"
              data-testid="kc-catalogue-download"
              onClick={() => trackEvent("datasheet_download", { document: "product_catalogue", from: "knowledge_center" })}
              className="group flex items-start gap-4 border border-line bg-white p-6 transition-colors hover:border-accent/40">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-line bg-mist text-accent"><FileDown size={19} /></span>
              <span>
                <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-accent">Catalogue · PDF</span>
                <span className="mt-1.5 block font-display text-base font-semibold text-ink group-hover:text-accent">STJ Product Catalogue</span>
                <span className="mt-1 block text-sm text-steel">The complete official catalogue: pass boxes, LAF units, doors, panels, air showers, AHUs, furniture and SmartReady™ systems.</span>
              </span>
            </a>
            {docs.map((d) => (
              <a key={d.id} href={d.file_url || "#"} target="_blank" rel="noopener noreferrer"
                data-testid={`kc-document-${d.slug}`}
                onClick={() => trackEvent("datasheet_download", { document: d.slug, from: "knowledge_center" })}
                className="group flex items-start gap-4 border border-line bg-white p-6 transition-colors hover:border-accent/40">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-line bg-mist text-accent"><FileDown size={19} /></span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-accent">{d.category}</span>
                  <span className="mt-1.5 block font-display text-base font-semibold text-ink group-hover:text-accent">{d.title}</span>
                  {d.description && <span className="mt-1 block text-sm text-steel">{d.description}</span>}
                  <span className="mt-1 block font-mono text-[10px] text-slate-400">
                    {[d.author, d.created_at ? new Date(d.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </a>
            ))}
          </div>
          <div className="mt-8 flex items-start gap-3 border border-line bg-white p-5" data-testid="kc-student-note">
            <GraduationCap size={18} className="mt-0.5 shrink-0 text-accent" />
            <p className="text-sm leading-relaxed text-steel">
              <span className="font-medium text-ink">Students & researchers:</span> all Knowledge Center material may be used for educational purposes with attribution to STJ Innovations. For project collaborations or additional technical material, contact our team.
            </p>
          </div>
        </div>
      </section>

      {/* Glossary */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20" data-testid="kc-glossary">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <SectionHead eyebrow="Glossary" title="Cleanroom & Engineering Terminology"
            copy="Plain-language definitions of the terms used across cleanroom and pharmaceutical engineering." />
          <div className="relative w-full sm:w-80">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="glossary-search" type="search" placeholder="Search terms…" value={gQuery}
              onChange={(e) => setGQuery(e.target.value)} className="input-field pl-9" aria-label="Search glossary" />
          </div>
        </div>
        <dl className="mt-10 grid gap-px border border-line bg-line sm:grid-cols-2" data-testid="glossary-grid">
          {filteredGlossary.map((g, i) => (
            <div key={i} className="bg-white p-6" data-testid={`glossary-term-${i}`}>
              <dt className="font-display text-base font-semibold text-ink">{g.term}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-steel">{g.definition}</dd>
            </div>
          ))}
        </dl>
        {filteredGlossary.length === 0 && (
          <p className="mt-6 border border-line bg-mist p-8 text-center text-sm text-steel">No terms match your search.</p>
        )}
      </section>

      <CTASection title="Have a Technical Question?"
        copy="Our engineering team answers application and specification questions directly — whether or not a project is attached." />
    </div>
  );
}
